import IdsElement from '../../core/ids-element';
import { attributes } from '../../core/ids-attributes';
import { customElement, scss } from '../../core/ids-decorators';
import { setBooleanAttr } from '../../utils/ids-attribute-utils/ids-attribute-utils';
import { Breakpoints, isWidthBelow } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';

import { RESPONSIVE_BREAKPOINT } from './ids-module-nav-common';

import IdsBreakpointMixin from '../../mixins/ids-breakpoint-mixin/ids-breakpoint-mixin';
import IdsModuleNavDisplayModeMixin from './ids-module-nav-display-mode-mixin';

import styles from './ids-module-nav.scss';

import type IdsContainer from '../ids-container/ids-container';
import type IdsModuleNavBar from './ids-module-nav-bar';
import type IdsModuleNavContent from './ids-module-nav-content';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';

import './ids-module-nav-bar';
import './ids-module-nav-button';
import './ids-module-nav-content';
import './ids-module-nav-item';
import './ids-module-nav-settings';
import './ids-module-nav-switcher';
import '../ids-search-field/ids-search-field';

const Base = IdsModuleNavDisplayModeMixin(
  IdsBreakpointMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Module Nav Component
 * @type {IdsModuleNav}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsBreakpointMixin
 * @mixes IdsModuleNavDisplayModeMixin
 */
@customElement('ids-module-nav')
@scss(styles)
export default class IdsModuleNav extends Base {
  constructor() {
    super();
  }

  async connectedCallback() {
    super.connectedCallback();
    this.configureResponsiveBehavior();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes as an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.RESPONSIVE,
      attributes.BREAKPOINT
    ];
  }

  template() {
    return `<div class="ids-module-nav">
      <slot></slot>
    </div>`;
  }

  /**
   * Get reference to the Module Nav bar
   * @returns {IdsModuleNavBar | undefined} the module nav bar
   */
  get bar() {
    const barEl = this.querySelector('ids-module-nav-bar');
    if (barEl?.tagName === 'IDS-MODULE-NAV-BAR') {
      return (barEl as IdsModuleNavBar);
    }
    return undefined;
  }

  /**
   * Get reference to the content pane
   * @returns {IdsModuleNavContent | undefined} the content pane
   */
  get content() {
    const contentEl = this.querySelector('ids-module-nav-content');
    if (contentEl?.tagName === 'IDS-MODULE-NAV-CONTENT') {
      return (contentEl as IdsModuleNavContent);
    }
    return undefined;
  }

  /**
   * Get reference to the Module Nav parent element
   * @returns {IdsContainer | undefined} the parent container
   */
  get parent() {
    const parentEl: IdsContainer | undefined = getClosest(this, 'ids-container');
    if (parentEl) return parentEl;
    return undefined;
  }

  /**
   * Set the breakpoint
   * @param {string} val if truthy, causes the Module Nav system to respond to a mobile breakpoint
   */
  set breakpoint(val: string) {
    if (val) {
      this.setAttribute(attributes.BREAKPOINT, val);
      this.content?.setAttribute(attributes.BREAKPOINT, val);
      this.configureResponsiveBehavior();
    } else {
      this.removeAttribute(attributes.BREAKPOINT);
      this.content?.removeAttribute(attributes.BREAKPOINT);
    }
  }

  /**
   * Get the breakpoint value
   * @returns {string} the breakpoint value (default to current breakpoint)
   */
  get breakpoint() {
    return this.getAttribute(attributes.BREAKPOINT) || RESPONSIVE_BREAKPOINT; // Default to current breakpoint
  }

  /**
   * @param {boolean | string} val if truthy, causes the Module Nav system to respond to a mobile breakpoint
   */
  set responsive(val: boolean | string) {
    setBooleanAttr(attributes.RESPONSIVE, this, val);
    this.configureResponsiveBehavior();
  }

  /**
   * Get whether responsive behavior is enabled
   * @returns {boolean | string} true if responsive
   */
  get responsive(): boolean | string {
    return this.hasAttribute(attributes.RESPONSIVE);
  }

  /**
   * Inherited from IdsModuleNavDisplayModeMixin
   */
  async onDisplayModeChange() {
    if (this.bar) this.bar.displayMode = this.displayMode;
    if (this.content) this.content.displayMode = this.displayMode;

    if (this.displayMode !== 'expanded') {
      await this.hideContentOverlay();
    } else if (this.isWithinMobileBreakpoint()) {
      this.content?.showOverlay();
    }

    this.triggerEvent('displaymodechange', this, {
      detail: {
        displayMode: this.displayMode,
        elem: this
      }
    });
  }

  /**
   * Responds to outside clicks established by the Module Nav bar
   * using the IDS Breakpoint system established in this component.
   */
  handleOutsideClick() {
    // Don't close the popup if md+ media query breakpoint
    if (this.displayMode === 'expanded' && this.isWithinMobileBreakpoint()) {
      this.displayMode = 'collapsed';
    }
  }

  /**
   * Sets up required parts for IdsBreakpointMixin-driven mobile behavior
   */
  private configureResponsiveBehavior() {
    if (this.responsive) {
      this.onBreakpointDownResponse = async (detectedBreakpoint: keyof Breakpoints, matches: boolean) => {
        if (matches) {
          this.handleBelowBreakpoint();
        } else {
          await this.handleAboveBreakpoint();
        }
      };
      if (!this.respondDown) this.respondDown = this.breakpoint || RESPONSIVE_BREAKPOINT;
    } else {
      this.onBreakpointDownResponse = () => {};
      this.respondDown = null;
    }
  }

  /**
   * Switches the Module Nav into its mobile behavior mode
   */
  private async handleAboveBreakpoint() {
    await this.hideContentOverlay();
    if (this.displayMode !== 'expanded') {
      this.displayMode = 'collapsed';
    }
    this.content!.offsetContent = true;
  }

  /**
   * Switches the Module Nav into its desktop behavior mode
   */
  private handleBelowBreakpoint() {
    if (this.displayMode === 'expanded') {
      this.content?.showOverlay();
    } else {
      this.displayMode = 'collapsed';
    }
    this.content!.offsetContent = true;
  }

  private async hideContentOverlay() {
    if (this.content && this.content.overlay!.visible) {
      await this.content.hideOverlay();
    }
  }

  /**
   * @returns {boolean} true if the mobile breakpoint conditions are currently being met
   */
  isWithinMobileBreakpoint() {
    const mq = isWidthBelow(this.breakpoint);
    return mq.matches;
  }
}
